import type { MeetingPlatform } from '../../../../types';
import { toISODateString } from '../../../../utils/dateHelpers';

export interface ClearableCohortValues {
  default_meeting_link: string;
  default_meeting_platform: string;
  participant_description: string;
  matching_form_link: string;
  matching_form_deadline: Date | null;
  cohort_wrap_up_date: Date | null;
}

export interface ClearableCohortBody {
  default_meeting_link?: string | null;
  default_meeting_platform?: MeetingPlatform | null;
  participant_description?: string | null;
  matching_form_link?: string | null;
  matching_form_deadline?: string | null;
  cohort_wrap_up_date?: string | null;
}

/** Each field as the PUT body carries it: the value, or null when empty. */
function toBody(values: ClearableCohortValues): Required<ClearableCohortBody> {
  return {
    default_meeting_link: values.default_meeting_link || null,
    default_meeting_platform: (values.default_meeting_platform || null) as MeetingPlatform | null,
    participant_description: values.participant_description || null,
    matching_form_link: values.matching_form_link || null,
    matching_form_deadline: toISODateString(values.matching_form_deadline) ?? null,
    cohort_wrap_up_date: toISODateString(values.cohort_wrap_up_date) ?? null,
  };
}

/**
 * MED-1360: the optional fields of Edit Cohort Details for the PUT body.
 *
 * Only a field that changed from the loaded value is sent. A stored value
 * the user cleared goes as null, so the API clears the column (before, it
 * went as undefined, which JSON leaves out, and the clear was lost). A field
 * the user did not change is left out, so a save never touches it: the API
 * regenerates task due dates whenever the body carries cohort_wrap_up_date.
 * Values compare as sent, so a wrap up date on the same day is no change.
 */
export function clearableCohortFields(
  values: ClearableCohortValues,
  loaded: ClearableCohortValues,
): ClearableCohortBody {
  const next = toBody(values);
  const before = toBody(loaded);
  const body: ClearableCohortBody = {};
  for (const key of Object.keys(next) as (keyof ClearableCohortBody)[]) {
    if (next[key] !== before[key]) {
      (body as Record<string, string | null>)[key] = next[key];
    }
  }
  return body;
}
